import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { EnvelopeSimple, Sparkle, X } from "@phosphor-icons/react";

const STATUSES = ["new", "contacted", "in_discussion", "won", "closed"];

export default function PartnerInquiries() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [showcaseFor, setShowcaseFor] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/partners/admin/list", { params: filter ? { status: filter } : {} }); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    const note = prompt("Optional note", "");
    try { await api.post(`/partners/admin/${id}/status`, { status, note: note || null }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const disableShowcase = async (id) => {
    try {
      await api.post(`/partners/admin/${id}/showcase`, { showcase_enabled: false });
      toast.success("Removed from showcase");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <Heading title="Partner Inquiries"
               subtitle="Inbound partnership requests from the public 'Become a Partner' CTA. Promote your won deals to the Landing showcase carousel." />

      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 text-xs rounded-sm border ${!filter ? "bg-nk-accent text-nk-bg border-nk-accent" : "text-nk-mute border-nk-line"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border ${filter === s ? "bg-nk-accent text-nk-bg border-nk-accent" : "text-nk-mute border-nk-line hover:border-nk-accent hover:text-nk-text"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-nk-dim">No inquiries{filter ? ` in '${filter}'` : ''}.</p>}
        {rows.map((r) => (
          <div key={r.inquiry_id} className="nk-card p-4" data-testid={`inquiry-row-${r.inquiry_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-display text-lg flex items-center gap-2">
                  {r.name}
                  <span className="text-[10px] text-nk-mute uppercase tracking-wider">· {r.partnership_type.replace("_", " ")}</span>
                  {r.showcase_enabled && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider bg-nk-gold/20 text-nk-gold inline-flex items-center gap-1">
                      <Sparkle size={9} weight="fill" /> Showcased
                    </span>
                  )}
                </p>
                <a href={`mailto:${r.email}`} className="text-xs text-nk-accent font-mono hover:underline inline-flex items-center gap-1">
                  <EnvelopeSimple size={11} /> {r.email}
                </a>
                <p className="text-[11px] text-nk-mute mt-1">
                  {r.company || "—"} · {r.role || "—"} · {r.country || "—"} · budget {r.budget_band}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                  r.status === "won" ? "bg-nk-gold/20 text-nk-gold" :
                  r.status === "closed" ? "bg-nk-line/40 text-nk-mute" :
                  r.status === "new" ? "bg-nk-accent/20 text-nk-accent" :
                  "bg-nk-warn/20 text-nk-warn"
                }`}>{(r.status || "new").replace("_", " ")}</span>
                <select value={r.status} onChange={(e) => setStatus(r.inquiry_id, e.target.value)}
                        className="text-[11px] bg-nk-bg border border-nk-line rounded-sm px-2 py-1">
                  {STATUSES.map((s) => <option key={s} value={s}>{`→ ${s.replace("_", " ")}`}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3 p-3 bg-nk-bg/40 border border-nk-line/40 rounded-sm text-sm whitespace-pre-wrap text-nk-text">
              {r.message}
            </div>
            <p className="text-[10px] text-nk-dim mt-2 font-mono">{new Date(r.created_at).toLocaleString()} · {r.inquiry_id}</p>
            {r.last_note && (
              <p className="text-[11px] text-nk-mute mt-1 italic">Last note: {r.last_note}</p>
            )}

            {/* Showcase controls — only for won inquiries */}
            {r.status === "won" && (
              <div className="mt-3 pt-3 border-t border-nk-gold/20 flex items-center gap-2 flex-wrap">
                {r.showcase_enabled ? (
                  <>
                    <span className="text-[11px] text-nk-gold flex items-center gap-1">
                      <Sparkle size={10} weight="fill" /> Live on Landing carousel
                    </span>
                    <button onClick={() => setShowcaseFor(r)}
                            data-testid={`showcase-edit-${r.inquiry_id}`}
                            className="text-[11px] px-2 py-1 border border-nk-line text-nk-mute hover:text-nk-text hover:border-nk-gold rounded-sm">
                      Edit
                    </button>
                    <button onClick={() => disableShowcase(r.inquiry_id)}
                            data-testid={`showcase-remove-${r.inquiry_id}`}
                            className="text-[11px] px-2 py-1 border border-nk-bad/40 text-nk-bad hover:bg-nk-bad/10 rounded-sm inline-flex items-center gap-1">
                      <X size={10} weight="bold" /> Remove
                    </button>
                  </>
                ) : (
                  <button onClick={() => setShowcaseFor(r)}
                          data-testid={`showcase-add-${r.inquiry_id}`}
                          className="text-[11px] px-2.5 py-1 bg-nk-gold/15 border border-nk-gold/40 text-nk-gold hover:bg-nk-gold/25 rounded-sm inline-flex items-center gap-1.5">
                    <Sparkle size={10} weight="fill" /> Add to Landing showcase
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showcaseFor && (
        <ShowcaseDialog inquiry={showcaseFor} onClose={() => setShowcaseFor(null)} onSaved={() => { setShowcaseFor(null); load(); }} />
      )}
    </div>
  );
}

function ShowcaseDialog({ inquiry, onClose, onSaved }) {
  const [form, setForm] = useState({
    showcase_logo_url: inquiry.showcase_logo_url || "",
    showcase_quote: inquiry.showcase_quote || "",
    showcase_link_url: inquiry.showcase_link_url || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.showcase_logo_url || !form.showcase_quote) {
      toast.error("Logo URL and quote are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/partners/admin/${inquiry.inquiry_id}/showcase`, {
        showcase_enabled: true,
        ...form,
      });
      toast.success("Showcased on Landing");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="showcase-dialog">
      <form onSubmit={save} className="w-full max-w-lg bg-nk-bg border border-nk-gold/40 rounded-sm p-6 space-y-4">
        <div>
          <p className="eyebrow text-nk-gold flex items-center gap-1.5 mb-1">
            <Sparkle size={11} weight="fill" /> Landing showcase
          </p>
          <h3 className="font-display text-2xl">{inquiry.company || inquiry.name}</h3>
          <p className="text-[11px] text-nk-mute">Promote this partner to the public carousel.</p>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-nk-mute">Logo URL *</label>
          <input value={form.showcase_logo_url}
                 onChange={(e) => setForm({ ...form, showcase_logo_url: e.target.value })}
                 placeholder="https://…/logo.png"
                 required maxLength={400}
                 data-testid="showcase-logo-url"
                 className="w-full mt-1 bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-nk-mute">Quote *</label>
          <textarea value={form.showcase_quote}
                    onChange={(e) => setForm({ ...form, showcase_quote: e.target.value })}
                    placeholder="A short one-liner from the partner."
                    required maxLength={240} rows={2}
                    data-testid="showcase-quote"
                    className="w-full mt-1 bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
          <p className="text-[10px] text-nk-dim text-right">{form.showcase_quote.length}/240</p>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-nk-mute">Link URL (optional)</label>
          <input value={form.showcase_link_url}
                 onChange={(e) => setForm({ ...form, showcase_link_url: e.target.value })}
                 placeholder="https://…"
                 maxLength={400}
                 className="w-full mt-1 bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-nk-line text-nk-mute hover:text-nk-text rounded-sm">
            Cancel
          </button>
          <button type="submit" disabled={busy}
                  data-testid="showcase-save"
                  className="flex-1 px-4 py-2 bg-nk-gold text-nk-bg font-medium text-sm rounded-sm hover:bg-nk-gold/90 disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
            <Sparkle size={11} weight="fill" /> {busy ? "Saving…" : "Publish to Landing"}
          </button>
        </div>
      </form>
    </div>
  );
}
